import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createPart, updatePart, fetchPartStatuses, fetchSuppliers, fetchStockParts, fetchValidation } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";

const NEEDED = "Needed";
const STOCK_SUPPLIER_NAME = "Stock Parts";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Remember the last part_type the user entered so sequential adds don't
// require re-selecting. Stored per-session (not across devices).
const LAST_TYPE_KEY = "tickdoc_last_part_type";
const getLastType = () => {
  try { return localStorage.getItem(LAST_TYPE_KEY) || ""; } catch { return ""; }
};
const setLastType = (t) => {
  try { if (t) localStorage.setItem(LAST_TYPE_KEY, t); } catch { /* ignore */ }
};

const EMPTY = {
  part_name: "", part_type: "", part_description: "", part_number: "",
  supplier: "", order_status: "Needed",
  order_date: "", received_date: "", quantity: 1, cost_each: "",
  tracking_number: "", notes: "",
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const intOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));
const str = (v) => (v === "" || v === null || v === undefined ? null : String(v));

export default function PartFormSheet({ open, onOpenChange, part, watchId, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockParts, setStockParts] = useState([]);
  const [partTypes, setPartTypes] = useState([]);

  useEffect(() => { fetchPartStatuses().then(setStatuses).catch(() => {}); }, []);
  useEffect(() => { fetchSuppliers({ active_only: true }).then((r) => setSuppliers(r.items || [])).catch(() => {}); }, []);
  useEffect(() => { fetchStockParts().then(setStockParts).catch(() => {}); }, []);
  useEffect(() => {
    // Load `Part Type` from the unified validation table so the dropdown
    // is user-managed via /data/validation. Sort alpha for predictability.
    fetchValidation()
      .then((v) => {
        const arr = (v["Part Type"] || []).slice().sort((a, b) => String(a).localeCompare(String(b)));
        setPartTypes(arr);
      })
      .catch(() => {});
  }, []);

  const onPickStock = (id) => {
    if (!id || id === "__none__") return;
    const sp = stockParts.find((s) => s.id === id);
    if (!sp) return;
    // Resolve a "Stock Parts" supplier name if the user has one set up — fall
    // back to the literal string so the supplier field is always populated
    // (the Supplier select supports custom values via a passthrough item).
    const stockSupplier = suppliers.find(
      (s) => String(s.name || "").trim().toLowerCase() === STOCK_SUPPLIER_NAME.toLowerCase(),
    );
    const supplierName = stockSupplier?.name || STOCK_SUPPLIER_NAME;
    const today = todayISO();
    // Only fill blanks — don't clobber what the user has already typed.
    setData((d) => ({
      ...d,
      part_name: d.part_name || sp.name,
      part_type: d.part_type || sp.part_type || d.part_type,
      cost_each: d.cost_each === "" || d.cost_each === null
        ? (sp.default_cost ?? "")
        : d.cost_each,
      // Stock items are by definition already on the bench: mark them
      // received today and attach the Stock Parts supplier when available.
      order_status: d.order_status === NEEDED || !d.order_status ? "Received" : d.order_status,
      supplier: d.supplier || supplierName,
      order_date: d.order_date || today,
      received_date: d.received_date || today,
    }));
  };

  useEffect(() => {
    if (part) {
      const seed = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        seed[k] = part[k] === null || part[k] === undefined ? "" : part[k];
      });
      setData(seed);
    } else {
      // New entry — pre-fill `part_type` from the last one the user picked
      // so adding a batch of the same kind of part is quick.
      setData({ ...EMPTY, part_type: getLastType() });
    }
  }, [part, open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const preview =
    data.cost_each && data.quantity
      ? (Number(data.cost_each) * Number(data.quantity)).toFixed(2)
      : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.part_name || !data.part_name.trim()) {
      toast.error("Part name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        watch_id: watchId,
        part_name: str(data.part_name),
        part_type: str(data.part_type),
        part_description: str(data.part_description),
        part_number: str(data.part_number),
        supplier: str(data.supplier),
        order_status: str(data.order_status) || "Needed",
        order_date: str(data.order_date),
        received_date: str(data.received_date),
        quantity: intOrNull(data.quantity) || 1,
        cost_each: num(data.cost_each),
        tracking_number: str(data.tracking_number),
        notes: str(data.notes),
      };
      if (part?.id) {
        await updatePart(part.id, payload);
        toast.success("Part updated");
      } else {
        await createPart(payload);
        toast.success("Part added");
      }
      if (data.part_type) setLastType(data.part_type);
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#FBF3D9] border-l border-[#8B6A1F]/45 text-[#0A0604] overflow-y-auto pb-10" data-testid="part-form-sheet">
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl">{part ? "Edit Part" : "Add Part"}</SheetTitle>
          <SheetDescription className="text-[#3A2A10] text-xs uppercase tracking-[0.25em]">Parts ledger</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="part-form">
          {/* Quick-pick from stock parts catalog. Selecting one pre-fills
              name / type / cost for the user — speeds up logging crystals,
              mainsprings, straps, etc. that are repeated across services. */}
          {!part && stockParts.length > 0 && (
            <Field label="Pick from stock parts (optional)">
              <Select value="__none__" onValueChange={onPickStock}>
                <SelectTrigger className="dark-input h-10" data-testid="part-form-stock-pick">
                  <SelectValue placeholder="Choose a stock item to pre-fill…" />
                </SelectTrigger>
                <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm max-h-[320px]">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {stockParts.map((sp) => (
                    <SelectItem key={sp.id} value={sp.id}>
                      {sp.name}
                      {sp.default_cost != null ? ` · $${sp.default_cost.toFixed(2)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Part name *">
            <Input value={data.part_name} onChange={(e) => set("part_name", e.target.value)} className="dark-input" required data-testid="part-form-name" />
          </Field>
          <Row2>
            <Field label="Type">
              <ComboSelect value={data.part_type} onChange={(v) => set("part_type", v)} options={partTypes} testid="part-form-type" />
            </Field>
            <Field label="Part number">
              <Input value={data.part_number} onChange={(e) => set("part_number", e.target.value)} className="dark-input" placeholder="e.g. 1872-721" data-testid="part-form-number" />
            </Field>
          </Row2>
          <Field label="Description">
            <Textarea
              value={data.part_description}
              onChange={(e) => set("part_description", e.target.value)}
              className="dark-input min-h-16"
              placeholder="Dimensions, alt references, seller's blurb…"
              data-testid="part-form-description"
            />
          </Field>
          <Field label="Status">
            <Select value={data.order_status || "Needed"} onValueChange={(v) => set("order_status", v)}>
              <SelectTrigger className="dark-input h-10" data-testid="part-form-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
                {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {data.order_status === NEEDED ? (
            <div className="p-3 border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] rounded-sm text-[11px] text-[#0A0604]" data-testid="part-form-sourcing-hint">
              <span className="uppercase tracking-wider text-[#3A2A10] font-mono-data text-[10px] mr-2">Sourcing</span>
              Supplier is set once you mark this part <span className="text-[#0A0604]">Ordered</span> — giving you time to shop around for the best price.
            </div>
          ) : (
            <Field label="Supplier *">
              <Select value={data.supplier || "__none__"} onValueChange={(v) => set("supplier", v === "__none__" ? "" : v)}>
                <SelectTrigger className="dark-input h-10" data-testid="part-form-supplier"><SelectValue placeholder="Choose supplier…" /></SelectTrigger>
                <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm max-h-[320px]">
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  {data.supplier && !suppliers.some((s) => s.name === data.supplier) && (
                    <SelectItem value={data.supplier}>{data.supplier} (custom)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Row2>
            <Field label="Quantity">
              <Input type="number" min="1" value={data.quantity} onChange={(e) => set("quantity", e.target.value)} className="dark-input" data-testid="part-form-qty" />
            </Field>
            <Field label="Cost each">
              <Input type="number" step="0.01" value={data.cost_each} onChange={(e) => set("cost_each", e.target.value)} className="dark-input" data-testid="part-form-cost" />
            </Field>
          </Row2>
          <Row2>
            <Field label="Order date">
              <Input type="date" value={data.order_date || ""} onChange={(e) => set("order_date", e.target.value)} className="dark-input" data-testid="part-form-order-date" />
            </Field>
            <Field label="Received date">
              <Input type="date" value={data.received_date || ""} onChange={(e) => set("received_date", e.target.value)} className="dark-input" data-testid="part-form-recv-date" />
            </Field>
          </Row2>
          <Row2>
            <Field label="Total">
              <div className="h-10 px-3 flex items-center border border-[#8B6A1F]/45 bg-[#F9F0D4] rounded-sm font-mono-data text-[#3A2A10]">
                {preview ? `$${preview}` : "—"}
              </div>
            </Field>
          </Row2>
          <Field label="Tracking number">
            <Input value={data.tracking_number} onChange={(e) => set("tracking_number", e.target.value)} className="dark-input" data-testid="part-form-tracking" />
          </Field>
          <Field label="Notes">
            <Textarea value={data.notes} onChange={(e) => set("notes", e.target.value)} className="dark-input min-h-16" data-testid="part-form-notes" />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#8B6A1F]/35">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-[#3A2A10] hover:text-[#0A0604]" data-testid="part-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] uppercase tracking-wider text-xs rounded-sm" data-testid="part-form-submit">
              {saving ? "Saving…" : part ? "Save Changes" : "Add Part"}
            </Button>
          </div>
        </form>

        <style>{`.dark-input{background-color: #F9F0D4 !important;border-color: rgba(139,106,31,0.25) !important;border-radius:2px!important;color: #2B2214 !important}.dark-input:focus,.dark-input:focus-visible{border-color:rgba(212,175,55,0.5)!important;outline:none!important;box-shadow:0 0 0 1px rgba(212,175,55,0.3)!important}`}</style>
      </SheetContent>
    </Sheet>
  );
}

function Row2({ children }) { return <div className="grid grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">{label}</Label>
      {children}
    </div>
  );
}
function DatalistInput({ value, onChange, options, listId, testid }) {
  return (
    <>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="dark-input" list={listId} data-testid={testid} />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

import SearchableCombobox from "@/components/SearchableCombobox";

function ComboSelect({ value, onChange, options = [], testid }) {
  return (
    <SearchableCombobox value={value} onChange={onChange} options={options} testid={testid} />
  );
}
